import { ref, computed } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import { getOrganizerRefundPreview, cancelRegistration } from '@/api/participants'
import type { OrganizerRefundPreview } from '@/types/participants'
import type { RefundBlockedReason, RefundFailure } from '@/types/refunds'

function isApiError(
  e: unknown,
): e is { response: { status: number; data: { errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  )
}

export interface UseOrganizerRefundReturn {
  refundPreview: Ref<OrganizerRefundPreview | null>
  refundAmount: Ref<number>
  isLoading: Ref<boolean>
  submitting: Ref<boolean>
  error: Ref<string | null>
  /** Set when the cancel took effect but Stripe rejected the refund. */
  refundFailure: Ref<RefundFailure | null>
  hasChangedFromDefault: ComputedRef<boolean>
  /** True when the registration has an online payment that could be refunded. */
  hasOnlinePayment: ComputedRef<boolean>
  /** Reason a refund is blocked while the registration still has an online payment, else null. */
  cancelBlockedReason: ComputedRef<RefundBlockedReason | null>
  /** True when a positive refund amount may be sent with the cancellation. */
  canRefundOnCancel: ComputedRef<boolean>
  fetchPreview: (registrationId: number) => Promise<void>
  confirm: (registrationId: number) => Promise<boolean>
  reset: () => void
}

export function useOrganizerRefund(): UseOrganizerRefundReturn {
  const refundPreview = ref<OrganizerRefundPreview | null>(null)
  const refundAmount = ref<number>(0)
  const isLoading = ref(false)
  const submitting = ref(false)
  const error = ref<string | null>(null)
  const refundFailure = ref<RefundFailure | null>(null)

  const hasChangedFromDefault = computed<boolean>(() => {
    if (refundPreview.value === null) return false
    return refundAmount.value !== parseFloat(refundPreview.value.policy_refund_amount)
  })

  const hasOnlinePayment = computed<boolean>(
    () => refundPreview.value?.has_online_payment === true,
  )

  const cancelBlockedReason = computed<RefundBlockedReason | null>(() => {
    const preview = refundPreview.value
    if (preview === null || !preview.has_online_payment) return null
    return preview.blocked_reason
  })

  const canRefundOnCancel = computed<boolean>(
    () => hasOnlinePayment.value && refundPreview.value?.blocked_reason === null,
  )

  async function fetchPreview(registrationId: number): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const preview = await getOrganizerRefundPreview(registrationId)
      refundPreview.value = preview
      refundAmount.value = parseFloat(preview.policy_refund_amount)
    } catch (e: unknown) {
      if (isApiError(e) && e.response.status === 403) {
        error.value = 'cancellation_policy.error_not_authorised'
      } else {
        error.value = 'common.error_generic'
      }
    } finally {
      isLoading.value = false
    }
  }

  async function confirm(registrationId: number): Promise<boolean> {
    submitting.value = true
    error.value = null
    refundFailure.value = null

    // Never send a positive amount without an online payment or while refunds are
    // blocked: the backend answers 422 for that.
    const amountToSend: number | null = canRefundOnCancel.value ? refundAmount.value : null

    try {
      const result = await cancelRegistration(registrationId, amountToSend)
      refundFailure.value = result.refundFailure
      return true
    } catch (e: unknown) {
      if (isApiError(e) && e.response.status === 422) {
        const errors = e.response.data.errors
        // A rejected Stripe refund is no longer a 422 (issue #215): it arrives
        // in a success response as refundFailure, so it is not mapped here.
        if (errors?.refund_amount) {
          error.value = 'participants.refund.error_amount_invalid'
        } else {
          error.value = 'common.error_generic'
        }
      } else if (isApiError(e) && e.response.status === 403) {
        error.value = 'cancellation_policy.error_not_authorised'
      } else {
        error.value = 'common.error_generic'
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  function reset(): void {
    refundPreview.value = null
    refundAmount.value = 0
    error.value = null
    refundFailure.value = null
  }

  return {
    refundPreview,
    refundAmount,
    isLoading,
    submitting,
    error,
    refundFailure,
    hasChangedFromDefault,
    hasOnlinePayment,
    cancelBlockedReason,
    canRefundOnCancel,
    fetchPreview,
    confirm,
    reset,
  }
}
